import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useMembers, useTasks } from '../../api/queries';
import { useHousehold } from '../../api/session';
import { Button, EmptyState, PageHeader, Spinner } from '../../components/ui';
import { TaskList } from './TaskCard';
import { TaskFormDialog } from './TaskFormDialog';

function NewTaskButton({ claim = false }: { claim?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus size={16} /> New task
      </Button>
      {open ? <TaskFormDialog open={open} onOpenChange={setOpen} defaultClaim={claim} /> : null}
    </>
  );
}

export function PoolPage() {
  const tasks = useTasks({ assignee: 'pool', sort: 'priority' });
  return (
    <>
      <PageHeader title="House Pool" subtitle="Things that need doing and nobody has taken yet." actions={<NewTaskButton />} />
      {tasks.isLoading ? <Spinner /> : <TaskList tasks={tasks.data?.items} empty={<EmptyState title="The House Pool is empty">Nice work, everyone.</EmptyState>} />}
    </>
  );
}

export function MineTasksPage() {
  const tasks = useTasks({ assignee: 'me', sort: 'due' });
  const done = useTasks({ assignee: 'me', status: 'completed', sort: 'completed' });
  return (
    <>
      <PageHeader title="My Tasks" subtitle="What you're handling. You can put anything back in the House Pool at any time." actions={<NewTaskButton claim />} />
      {tasks.isLoading ? <Spinner /> : <TaskList tasks={tasks.data?.items} empty={<EmptyState title="Nothing on your list">Pick something from the <Link to="/pool" className="text-[var(--accent)] underline">House Pool</Link>.</EmptyState>} />}
      {done.data?.items.length ? (
        <section className="mt-8">
          <h2 className="mb-2 text-sm font-medium text-[var(--muted)]">Recently completed</h2>
          <TaskList tasks={done.data.items.slice(0, 5)} empty={null} />
        </section>
      ) : null}
    </>
  );
}

export function PartnerTasksPage() {
  const { memberId } = useHousehold();
  const members = useMembers().data;
  const partners = (members ?? []).filter((m) => m.role === 'ADULT' && m.id !== memberId);
  const partner = partners[0];
  const tasks = useTasks({ assignee: partner?.id ?? 'none', sort: 'due' }, !!partner);
  if (!members) return <Spinner />;
  if (!partner) {
    return (
      <>
        <PageHeader title="Partner" />
        <EmptyState title="No partner yet">
          <Link to="/settings" className="text-[var(--accent)] underline">Invite your partner</Link> to share the household work.
        </EmptyState>
      </>
    );
  }
  return (
    <>
      <PageHeader
        title={`${partner.displayName}'s tasks`}
        subtitle="You can see everything, comment, or propose a different priority — but only they can change their own list."
      />
      {tasks.isLoading ? <Spinner /> : <TaskList tasks={tasks.data?.items} empty={<EmptyState title={`${partner.displayName} has nothing on their list`} />} />}
    </>
  );
}
